// src/pages/gerenciar/MudarEmpresa.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";

import { db } from "../../config/firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

import "./MudarEmpresa.css";

function MudarEmpresa() {
  const navigate = useNavigate();
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { modoGlobal, empresaAtual, definirGlobal, definirEmpresa } =
    useEmpresa();

  // Para Gestor: empresas do grupo com {id, nome}
  const [empresasGrupo, setEmpresasGrupo] = useState([]);
  const [carregandoEmpresas, setCarregandoEmpresas] = useState(false);
  const [erro, setErro] = useState("");

  const nivel = nivelUsuario(usuario);
  const empresaBaseLabel =
    usuario?.grupoNome || usuario?.empresaNome || usuario?.empresa || "Empresa base";

  // Kiosk não pode acessar esta tela
  useEffect(() => {
    if (nivel === NIVEIS.KIOSK) {
      navigate("/ordens-servico/abrir-nova-os", { replace: true });
    }
  }, [nivel, navigate]);

  // estado local para o select (empresaId)
  const [empresaSelecionada, setEmpresaSelecionada] = useState("");

  const temAcessoGlobal =
    usuario?.acessoGlobal === true ||
    usuario?.globalAccess === true ||
    usuario?.tipoAcesso === "GLOBAL";

  // Qualquer usuário logado (exceto Kiosk) deve conseguir alternar o contexto
  // entre Global e Cliente específico (dentro do seu escopo).
  const podeConfigurar = nivel >= NIVEIS.CLIENTE;

  const isGestor = nivel === NIVEIS.GESTOR;
  const isAdmin = nivel === NIVEIS.ADMIN;
  const isTecSenior = nivel === NIVEIS.TECNICO_SENIOR;

  // Empresas que este usuário pode enxergar em modo "empresa específica"
  const empresasPermitidas = useMemo(() => {
    if (!usuario) return [];

    const normalizar = (raw) => {
      if (!raw) return null;
      // aceita string (nome) OU objeto {id, nome}
      if (typeof raw === "string") {
        const nome = raw.trim();
        return nome ? { id: null, nome } : null;
      }
      if (typeof raw === "object") {
        const nome = String(raw.nome ?? raw.empresaNome ?? raw.empresa ?? "").trim();
        const id = raw.id ?? raw.empresaId ?? null;
        return nome ? { id, nome } : null;
      }
      return null;
    };

    const base = [];

    // empresa base do usuário (preferência por id/nome)
    const baseEmpresa = normalizar({
      empresaId: usuario.empresaId ?? null,
      empresaNome: usuario.empresaNome ?? usuario.empresa ?? usuario?.perfil?.empresa ?? null,
    });
    if (baseEmpresa) base.push(baseEmpresa);

    // lista extra
    const extras = Array.isArray(usuario.empresasPermitidas)
      ? usuario.empresasPermitidas
      : [];
    extras.forEach((raw) => {
      const emp = normalizar(raw);
      if (!emp) return;

      // evita duplicidade por id (se existir) ou por nome
      const jaTem = base.some((b) => (emp.id && b.id ? b.id === emp.id : b.nome === emp.nome));
      if (!jaTem) base.push(emp);
    });

    return base;
  }, [usuario]);

  // Lista que alimenta o select:
  // Gestor => empresas do grupo; demais => empresasPermitidas
  const listaEmpresasSelect = useMemo(() => {
    if (isGestor && empresasGrupo.length > 0) return empresasGrupo;
    return empresasPermitidas;
  }, [isGestor, empresasGrupo, empresasPermitidas]);

  // Sincroniza o select com a empresa atual do contexto
  useEffect(() => {
    if (!empresaAtual) {
      setEmpresaSelecionada("");
      return;
    }

    // tenta bater por id; se não tiver id, bate por nome
    const match = listaEmpresasSelect.find((e) => {
      if (empresaAtual.id && e.id) return e.id === empresaAtual.id;
      return e.nome === empresaAtual.nome;
    });

    setEmpresaSelecionada(match ? (match.id || match.nome) : "");
  }, [empresaAtual, listaEmpresasSelect]);

  // Para Gestor: carrega TODAS as empresas do grupo (para exibir na descrição)
  useEffect(() => {
    async function carregarEmpresasGrupo() {
      if (!usuario?.grupoId) return;
      if (!isGestor) return;

      setCarregandoEmpresas(true);
      setErro("");

      try {
        const empresasRef = collection(db, "empresas");
        const q = query(empresasRef, where("grupoId", "==", usuario.grupoId));
        const snap = await getDocs(q);

        const lista = snap.docs.map((docSnap) => {
          const dados = docSnap.data();
          return {
            id: docSnap.id,
            nome: dados.nome || "(sem nome)",
          };
        });

        setEmpresasGrupo(lista);
      } catch (e) {
        console.error("Erro ao carregar empresas do grupo:", e);
        setErro("Falha ao carregar empresas do grupo. Tente novamente.");
      } finally {
        setCarregandoEmpresas(false);
      }
    }

    carregarEmpresasGrupo();
  }, [usuario, isGestor]);

  const descricaoGlobal = useMemo(() => {
    if (!usuario) {
      return "Defina se o acesso será global ou restrito a um cliente.";
    }

    if (isGestor) {
      return `Global: todos os clientes da empresa base ${empresaBaseLabel}.`;
    }

    if (!isGestor) {
      return "Global: consolida os dados de todos os clientes dentro do seu escopo (cliente base + clientes adicionais).";
    }

    return `Global: todos os clientes da empresa base ${empresaBaseLabel}.`;
  }, [usuario, isGestor, isAdmin, isTecSenior, empresaBaseLabel]);

  const descricaoEmpresaAtual = useMemo(() => {
    if (!empresaAtual?.nome) {
      return "Nenhum cliente específico selecionado. O acesso segue o modo global.";
    }
    return `Cliente específico selecionado: ${empresaAtual.nome}`;
  }, [empresaAtual]);

  if (!usuario) {
    return <div>Carregando usuário...</div>;
  }

  if (!podeConfigurar) {
    return (
      <LayoutDesktop
        tema={tema}
        titulo="Acesso por Cliente"
        subtitulo="Gerenciar / Clientes"
      >
        <section className="pc-card">
          <h3 style={{ marginBottom: 8 }}>Acesso restrito</h3>
          <p style={{ fontSize: 14, lineHeight: 1.5 }}>
            Seu perfil já está configurado para enxergar os clientes nos quais
            você tem permissão. Caso necessite alterar esse acesso, procure um
            gestor ou administrador do sistema.
          </p>
        </section>
      </LayoutDesktop>
    );
  }

  const handleSelecionarGlobal = () => {
    definirGlobal();
  };

  const handleSelecionarEmpresa = (empresa) => {
    definirEmpresa(empresa);
  };

  const handleAplicarEmpresa = () => {
    if (!empresaSelecionada) return;
    // empresaSelecionada pode ser id (preferencial) ou nome (fallback)
    const empresaObj = listaEmpresasSelect.find((e) => {
      if (e.id) return e.id === empresaSelecionada;
      return e.nome === empresaSelecionada;
    });
    if (!empresaObj) return;
    handleSelecionarEmpresa(empresaObj);
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Acesso por Cliente"
      subtitulo="Gerenciar / Acesso Global x Cliente"
    >
      {/* Filtro removido */}

      <section className="pc-card">
        <header className="pc-card-header">
          <h3>Configurar escopo de acesso</h3>
          <p style={{ fontSize: 14 }}>
            Defina se você deseja ver os dados de forma global ou focar em um
            cliente específico.
          </p>
        </header>

        <div className="mudar-empresa-grid">
          {/* Coluna esquerda: modos */}
          <div>
            <div
              className="pc-card-inner"
              style={{
                padding: 16,
                borderRadius: 14,
                boxShadow: "var(--shadow-soft)",
                marginBottom: 16,
                border: modoGlobal
                  ? "2px solid var(--cor-accent, #22c55e)"
                  : "1px solid rgba(148, 163, 184, 0.35)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <h4 style={{ marginBottom: 6 }}>Modo Global</h4>
                  <p style={{ fontSize: 13, opacity: 0.85 }}>
                    {descricaoGlobal}
                  </p>
                </div>
                <div>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name="escopo"
                      checked={modoGlobal}
                      onChange={handleSelecionarGlobal}
                    />
                    <span>Ativar</span>
                  </label>
                </div>
              </div>

              {isGestor && (
                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                  {carregandoEmpresas
                    ? "Carregando clientes do grupo..."
                    : empresasGrupo.length > 0
                    ? `Clientes do grupo: ${empresasGrupo
                        .map((e) => e.nome)
                        .join(", ")}`
                    : "Nenhum cliente encontrado para este grupo ainda."}
                </div>
              )}

              {!isGestor && empresasPermitidas.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                  Clientes sob seu escopo: {empresasPermitidas
                    .map((e) => e.nome)
                    .join(", ")}
                </div>
              )}
            </div>

            <div
              className="pc-card-inner"
              style={{
                padding: 16,
                borderRadius: 14,
                boxShadow: "var(--shadow-soft)",
                border: !modoGlobal
                  ? "2px solid var(--cor-accent, #22c55e)"
                  : "1px solid rgba(148, 163, 184, 0.35)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <h4 style={{ marginBottom: 6 }}>Cliente específico</h4>
                  <p style={{ fontSize: 13, opacity: 0.85 }}>
                    Escolha um cliente para focar os dados (painéis, OS,
                    indicadores).
                  </p>
                </div>
                <div>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name="escopo"
                      checked={!modoGlobal}
                      onChange={() => {
                        if (listaEmpresasSelect[0]) {
                          handleSelecionarEmpresa(listaEmpresasSelect[0]);
                        } else {
                          definirEmpresa(null);
                        }
                      }}
                    />
                    <span>Ativar</span>
                  </label>
                </div>
              </div>

              {listaEmpresasSelect.length === 0 ? (
                <p style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                  Não há clientes específicos configurados para o seu usuário.
                  Verifique com o gestor/administrador.
                </p>
              ) : (
                <div style={{ marginTop: 12 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      fontSize: 12,
                      opacity: 0.85,
                    }}
                  >
                    Selecione o cliente:
                  </label>
                  <div className="mudar-empresa-select-row">
                    <select
                      disabled={false}
                      value={empresaSelecionada || ""}
                      onChange={(e) => setEmpresaSelecionada(e.target.value)}
                      style={{
                        flex: 1,
                        padding: "8px 10px",
                        borderRadius: 10,
                        border:
                          !modoGlobal && empresaAtual
                            ? "2px solid var(--cor-accent, #22c55e)"
                            : "1px solid var(--cor-borda-input, rgba(148, 163, 184, 0.35))",
                        background: "var(--cor-input-bg, transparent)",
                        color: "var(--cor-input-texto, inherit)",
                        fontSize: 13,
                        cursor: modoGlobal ? "not-allowed" : "pointer",
                      }}
                    >
                      <option value="" disabled>
                        Escolha um cliente...
                      </option>
                      {listaEmpresasSelect.map((empresa) => (
                        <option
                          key={empresa.id || empresa.nome}
                          value={empresa.id || empresa.nome}
                        >
                          {empresa.nome}
                        </option>
                      ))}
                    </select>

                    {/* Botão Aplicar */}
                    <button
                      type="button"
                      onClick={handleAplicarEmpresa}
                      disabled={!podeConfigurar || !empresaSelecionada}
                      className="mudar-empresa-btn-aplicar"
                      style={{
                        padding: "8px 14px",
                        borderRadius: 999,
                        border: "none",
                        fontSize: 13,
                        cursor:
                          !podeConfigurar || !empresaSelecionada
                            ? "not-allowed"
                            : "pointer",
                        boxShadow: "var(--shadow-soft)",
                        background:
                          !podeConfigurar || !empresaSelecionada
                            ? "rgba(148, 163, 184, 0.6)"
                            : "var(--cor-accent, #2563eb)",
                        color: "#fff",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Aplicar
                    </button>
                  </div>

                  {modoGlobal && (
                      <p style={{ marginTop: 10, fontSize: 12, opacity: 0.85 }}>
                        Dica: mesmo em modo global, você pode escolher um cliente para definir o contexto do app.
                      </p>
                    )}
                </div>
              )}
            </div>

            {erro && (
              <p style={{ marginTop: 12, fontSize: 12, color: "#f97373" }}>
                {erro}
              </p>
            )}
          </div>

          {/* Coluna direita: resumo */}
          <aside
            className="pc-card-inner"
            style={{
              padding: 16,
              borderRadius: 14,
              boxShadow: "var(--shadow-soft)",
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            <h4 style={{ marginBottom: 8 }}>Resumo do escopo atual</h4>
            <p>
              <strong>Usuário:</strong> {usuario?.nome || usuario?.email}
            </p>
            <p>
              <strong>Nível:</strong>{" "}
              {nivel === NIVEIS.GESTOR
                ? "Gestor"
                : nivel === NIVEIS.ADMIN
                ? "Administrador"
                : nivel === NIVEIS.TECNICO_SENIOR
                ? "Técnico Sênior"
                : "Outro"}
            </p>
            <p>
              <strong>Modo atual:</strong>{" "}
              {modoGlobal ? "Global" : "Cliente específico"}
            </p>
            <p>{descricaoEmpresaAtual}</p>

            {isGestor && usuario?.grupoId && (
              <p style={{ marginTop: 8 }}>
                <strong>Grupo atual:</strong> {usuario.grupoNome || usuario.grupoId}
              </p>
            )}
          </aside>
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default MudarEmpresa;
